// src/components/BulkOperations/BulkResultsList.js
import React from "react";
import PropTypes from "prop-types";
import BulkDeviceResultRow from "./BulkDeviceResultRow";
import logger from "../../utils/logger";

/**
 * BulkResultsList - List of device results for bulk operations
 *
 * Displays all device results with their cloud and device status
 */
const BulkResultsList = ({
  actionResults,
  actionStatuses,
  deviceStates,
  deviceLockStates,
  actionType,
  deviceTimers,
}) => {
  if (!actionResults || actionResults.length === 0) {
    return <p className="no-results">No results to display</p>;
  }

  const expectedState =
    actionType === "LOCK_DOWN" ? "Lockdown On" : "Lockdown Off";

  logger.log("[BulkResultsList] Rendering results:", {
    count: actionResults.length,
    expectedState,
    actionType,
  });

  // Calculate average response times
  const calculateAverages = () => {
    let totalCloudTime = 0;
    let totalDeviceTime = 0;
    let cloudCount = 0;
    let deviceCount = 0;

    actionResults.forEach((result) => {
      const timer = deviceTimers[result.deviceId];
      if (timer) {
        if (timer.cloudStopped && timer.cloudElapsed > 0) {
          totalCloudTime += timer.cloudElapsed;
          cloudCount++;
        }
        if (timer.deviceStopped && timer.deviceElapsed > 0) {
          totalDeviceTime += timer.deviceElapsed;
          deviceCount++;
        }
      }
    });

    const avgCloud = cloudCount > 0 ? totalCloudTime / cloudCount : 0;
    const avgDevice = deviceCount > 0 ? totalDeviceTime / deviceCount : 0;

    const formatTime = (ms) => {
      if (ms <= 0) return "---";
      const totalSeconds = ms / 1000;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = (totalSeconds % 60).toFixed(3);
      return `${String(minutes).padStart(2, "0")}:${seconds.padStart(6, "0")}`;
    };

    return {
      cloud: formatTime(avgCloud),
      device: formatTime(avgDevice),
    };
  };

  const averages = calculateAverages();

  return (
    <div className="bulk-results-list">
      <div className="results-header">
        <span className="header-device-name">Device Name</span>
        <span className="header-device-id">Device ID</span>
        <span className="header-action-id">Action ID</span>
        <span className="header-action-status">Action Status</span>
        <span className="header-lockdown-state">Lockdown State</span>
        <span className="header-lock-state">Lock State</span>
        <span className="header-cloud-time">
          <span className="header-icon">⏱ Cloud</span>
          <span className="header-label">Response Time</span>
        </span>
        <span className="header-device-time">
          <span className="header-icon">⏱ Device</span>
          <span className="header-label">Reaction Time</span>
        </span>
      </div>

      <div className="results-body">
        {actionResults.map((result) => (
          <BulkDeviceResultRow
            key={result.deviceId}
            result={result}
            deviceState={deviceStates[result.deviceId]}
            actionStatus={actionStatuses[result.actionId]}
            deviceLockState={deviceLockStates[result.deviceId]}
            expectedState={expectedState}
            deviceTimer={deviceTimers[result.deviceId]}
          />
        ))}
      </div>

      {/* Average Response Time Row */}
      <div className="average-response-row">
        <span className="avg-label" style={{ gridColumn: "1 / 7" }}>
          Average Response Time
        </span>
        <span className="avg-cloud-time">
          <span className="time-icon">⏱</span> {averages.cloud}
        </span>
        <span className="avg-device-time">
          <span className="time-icon">⏱</span> {averages.device}
        </span>
      </div>
    </div>
  );
};

BulkResultsList.propTypes = {
  actionResults: PropTypes.array.isRequired,
  actionStatuses: PropTypes.object.isRequired,
  deviceStates: PropTypes.object.isRequired,
  deviceLockStates: PropTypes.object.isRequired,
  actionType: PropTypes.string.isRequired,
  deviceTimers: PropTypes.object.isRequired,
};

export default BulkResultsList;
